import { v4 as uuidv4 } from 'uuid';

const SESSION_KEY = 'access_control_session';
const TOKEN_KEY = 'access_control_token';
const USER_KEY = 'access_control_user';

// TTL constants (in milliseconds)
const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours for session ID
const TOKEN_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours for token
const USER_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours for user data

// Helper to get TTL for a specific key
function getTTLForKey(key) {
    if (key === SESSION_KEY) {
        return SESSION_TTL_MS;
    } else if (key === TOKEN_KEY || key === USER_KEY) {
        return TOKEN_TTL_MS; // Same TTL for token and user
    }
    return null; // No TTL for unknown keys
}

// Helper to check if data is expired
function isDataExpired(createdAt, ttlMs) {
    if (!createdAt || !ttlMs) return false;
    const now = Date.now();
    const age = now - createdAt;
    return age > ttlMs;
}

// Storage helpers with fallback: sessionStorage first, then localStorage
// This ensures compatibility with private mode, in-app browsers, and older browsers
// Now includes TTL/expiry validation for all session data
function getSessionStorageItem(key) {
    let rawValue = null;
    
    try {
        // Try sessionStorage first
        rawValue = window.sessionStorage.getItem(key);
    } catch (error) {
        // Log specific error type for debugging
        if (error && error.name === 'QuotaExceededError') {
            console.warn('SessionStorage quota exceeded for', key, '- falling back to localStorage');
        } else if (error && error.name === 'SecurityError') {
            console.warn('SessionStorage security error for', key, '(possibly private mode) - falling back to localStorage');
        } else if (error) {
            console.warn('SessionStorage error for', key, ':', error.name || error.message, '- falling back to localStorage');
        }
        
        // Fallback to localStorage if sessionStorage fails
        try {
            rawValue = window.localStorage.getItem(key);
        } catch (localError) {
            if (localError && localError.name === 'QuotaExceededError') {
                console.warn('LocalStorage quota exceeded for', key);
            } else if (localError && localError.name === 'SecurityError') {
                console.warn('LocalStorage security error for', key);
            } else if (localError) {
                console.warn('LocalStorage error for', key, ':', localError.name || localError.message);
            }
            return null;
        }
    }
    
    if (!rawValue) {
        return null;
    }
    
    // Try to parse as JSON (for TTL-enabled data)
    try {
        const parsed = JSON.parse(rawValue);
        // Check if it's a TTL-enabled object
        if (parsed && typeof parsed === 'object' && 'value' in parsed && 'createdAt' in parsed) {
            const ttl = getTTLForKey(key);
            if (ttl && isDataExpired(parsed.createdAt, ttl)) {
                // Data is expired, remove it and return null
                console.warn('Storage data expired for', key, '- age:', Math.round((Date.now() - parsed.createdAt) / 1000 / 60), 'minutes');
                removeSessionStorageItem(key);
                return null;
            }
            // Data is valid, return the value
            return parsed.value;
        }
    } catch (_) {
        // Not JSON, return as-is (backwards compatibility)
    }
    
    // Return raw value if not TTL-enabled
    return rawValue;
}

function setSessionStorageItem(key, value) {
    let sessionSuccess = false;
    let localSuccess = false;
    
    // Check if this key should have TTL
    const ttl = getTTLForKey(key);
    let valueToStore = value;
    
    // If TTL is enabled, wrap value in object with timestamp
    if (ttl) {
        valueToStore = JSON.stringify({
            value: value,
            createdAt: Date.now()
        });
    }
    
    // Try sessionStorage first
    try {
        window.sessionStorage.setItem(key, valueToStore);
        sessionSuccess = true;
    } catch (error) {
        // Log specific error type for debugging
        if (error && error.name === 'QuotaExceededError') {
            console.warn('SessionStorage quota exceeded for', key, '- trying localStorage fallback');
        } else if (error && error.name === 'SecurityError') {
            console.warn('SessionStorage security error for', key, '(possibly private mode) - trying localStorage fallback');
        } else if (error) {
            console.warn('SessionStorage error for', key, ':', error.name || error.message, '- trying localStorage fallback');
        }
        
        // If sessionStorage fails, try localStorage as fallback
        try {
            window.localStorage.setItem(key, valueToStore);
            localSuccess = true;
        } catch (localError) {
            // Both storages failed - log specific error types
            if (localError && localError.name === 'QuotaExceededError') {
                console.warn('Both storages quota exceeded for', key);
            } else if (localError && localError.name === 'SecurityError') {
                console.warn('Both storages security error for', key);
            } else {
                console.warn('Failed to store in both sessionStorage and localStorage:', key, {
                    sessionError: error.name || error.message,
                    localError: localError.name || localError.message
                });
            }
        }
    }
    
    // Return true if at least one storage succeeded
    return sessionSuccess || localSuccess;
}

function removeSessionStorageItem(key) {
    // Remove from both storages to ensure cleanup
    try {
        window.sessionStorage.removeItem(key);
    } catch (_) { /* noop */ }
    
    try {
        window.localStorage.removeItem(key);
    } catch (_) { /* noop */ }
}

const sessionManager = {
    // Create a session id for this browser session to tag API calls
    // This correlates all API calls for a specific QR scan session
    // CRITICAL: Must always return a valid UUID for backend correlation (X-Session-ID header)
    // Includes TTL validation: session ID expires after 24 hours
    getSession: () => {
        let sessionId = getSessionStorageItem(SESSION_KEY);
        
        // Check if session ID is expired (handled in getSessionStorageItem)
        // If expired, it will return null and we generate a new one
        if (!sessionId) {
            // Generate new session ID
            sessionId = uuidv4();
            // Try to store it (may fail if both storages are unavailable)
            const stored = setSessionStorageItem(SESSION_KEY, sessionId);
            if (!stored) {
                // If storage failed, we still return the UUID but can't persist it
                // This ensures backend always receives a valid X-Session-ID header
                console.warn('Could not persist session ID to storage, but returning generated UUID for this request');
            } else {
                console.debug('New session ID generated and stored:', sessionId.substring(0, 8) + '...');
            }
        }
        
        // Always return a valid UUID, even if storage failed
        return sessionId;
    },

    // Backwards-compat alias expected by providers in some branches
    getCurrentSession: () => {
        return sessionManager.getSession();
    },

    setSession: (session) => {
        setSessionStorageItem(SESSION_KEY, session);
    },

    clearSession: () => {
        removeSessionStorageItem(SESSION_KEY);
        removeSessionStorageItem(TOKEN_KEY);
        removeSessionStorageItem(USER_KEY);
    },

    // Session-based token (engangs-token)
    getToken: () => {
        return getSessionStorageItem(TOKEN_KEY);
    },

    setToken: (token) => {
        setSessionStorageItem(TOKEN_KEY, token);
    },

    // Session-based user data
    getUser: () => {
        const raw = getSessionStorageItem(USER_KEY);
        try {
            return raw ? JSON.parse(raw) : null;
        } catch (_) {
            return raw;
        }
    },

    setUser: (user) => {
        const value = typeof user === 'string' ? user : JSON.stringify(user);
        setSessionStorageItem(USER_KEY, value);
    },

    isAuthenticated: () => {
        const token = getSessionStorageItem(TOKEN_KEY);
        return !!token;
    },

    logout: () => {
        removeSessionStorageItem(SESSION_KEY);
        removeSessionStorageItem(TOKEN_KEY);
        removeSessionStorageItem(USER_KEY);
        try { window.location.href = '/login'; } catch (_) { /* noop */ }
    },

    initializeSession: (data) => {
        if (data && data.token) {
            setSessionStorageItem(TOKEN_KEY, data.token);
        }
        if (data && data.user) {
            const value = typeof data.user === 'string' ? data.user : JSON.stringify(data.user);
            setSessionStorageItem(USER_KEY, value);
        }
        if (data && data.session) {
            setSessionStorageItem(SESSION_KEY, data.session);
        }
        // Ensure we always have a session id
        sessionManager.getSession();
    }
};

export { sessionManager };


